import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import dashboardService from '@services/dashboardService';
import { CalendarEvent } from '@/types/dashboard';
import { MONTHS_PT, WEEKDAYS_PT, buildMonthGrid, isoForDay } from '@components/dateInputUtils';
import {
  eventChipClasses,
  eventRefPrefix,
  eventTypeLabel,
  groupEventsByDay,
  monthParam,
  routeForEvent,
  shiftMonth,
} from './calendarUtils';

/**
 * Calendário de Prioridades — painel do dashboard (todos os perfis).
 *
 * Mostra por dia: compras prioritárias e previsões de entrega de fornecedores
 * (data prevista do PC), vendas prioritárias (data necessária) e vendas com
 * envio agendado. Clicar num item navega para o pedido correspondente.
 * Grade mensal própria (reaproveita a lógica do DateInputBR) — sem lib nova.
 */
export const DashboardCalendarPanel: React.FC = () => {
  // Mês inicial = hoje no relógio local (sem parsing de ISO — sem risco de fuso).
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadMonth = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const calendar = await dashboardService.getCalendar(monthParam(y, m));
      setEvents(calendar.events ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar o calendário');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonth(year, month);
  }, [year, month, loadMonth]);

  const changeMonth = (delta: number) => {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  };

  const byDay = groupEventsByDay(events);
  const cells = buildMonthGrid(year, month);
  const todayIso = isoForDay(now.getFullYear(), now.getMonth(), now.getDate());

  const legend: { type: CalendarEvent['type']; label: string }[] = [
    { type: 'purchase_priority', label: 'Compra prioritária' },
    { type: 'purchase_expected', label: 'Entrega de fornecedor' },
    { type: 'sales_priority', label: 'Venda prioritária' },
    { type: 'sales_scheduled_ship', label: 'Envio agendado' },
    { type: 'goods_delivery_expected', label: 'Entrega avulsa' },
    { type: 'goods_delivery_emergency', label: 'Entrega avulsa — emergência' },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-slate-500" />
          <h2 className="text-base font-semibold text-slate-800">Calendário de Prioridades</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            aria-label="Mês anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center">
            {MONTHS_PT[month]} {year}
          </span>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            aria-label="Próximo mês"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
            {WEEKDAYS_PT.map((wd) => (
              <div key={wd} className="bg-slate-50 text-center text-xs font-medium text-slate-500 py-1.5">
                {wd}
              </div>
            ))}
            {cells.map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} className="bg-white min-h-[72px]" />;
              }
              const iso = isoForDay(year, month, day);
              const dayEvents = byDay[iso] ?? [];
              const isToday = iso === todayIso;
              return (
                <div
                  key={iso}
                  className={`bg-white min-h-[72px] p-1 ${isToday ? 'ring-2 ring-inset ring-blue-400' : ''}`}
                >
                  <div className={`text-xs mb-1 ${isToday ? 'font-bold text-blue-600' : 'text-slate-500'}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.map((ev) => (
                      <button
                        key={`${ev.type}-${ev.id}`}
                        type="button"
                        onClick={() => navigate(routeForEvent(ev))}
                        title={`${eventTypeLabel(ev.type)} — ${eventRefPrefix(ev.type)} #${ev.id}${ev.label ? ` · ${ev.label}` : ''}`}
                        className={`w-full text-left truncate rounded px-1 py-0.5 text-[11px] leading-tight hover:opacity-80 ${eventChipClasses(ev.type)}`}
                      >
                        {ev.is_priority && <AlertTriangle size={9} className="inline mr-0.5 -mt-0.5" />}
                        {eventRefPrefix(ev.type)} #{ev.id}
                        {ev.label ? ` · ${ev.label}` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3 mt-3">
            {legend.map((item) => (
              <span key={item.type} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <span className={`inline-block w-3 h-3 rounded ${eventChipClasses(item.type)}`} />
                {item.label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
